import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * MedPayModal
 *
 * Med Pay reimbursement for one insurance row: generate the two letters and
 * open a reimbursement negotiation.
 *
 * The sequence (firm rule 11-B9) is:
 *   1. waiver request — the client was not made whole, so waive reimbursement
 *   2. common fund    — if they refuse, reduce by their pro rata share
 *   3. payment cover letter — deferred, not built
 *
 * Two things cannot be pulled from Filevine and are typed here:
 *   - the carrier's Med-Pay Subrogation contact. The adjuster on the
 *     insurance row handles the claim, not reimbursement.
 *   - the injury and treatment narrative.
 *
 * Everything else pre-fills from /medpay-context: settlement, fees computed
 * per rule 11-B8, costs, and med-pay paid summed from the per-medication
 * overlay (Filevine does not track Med Pay at the provider level).
 */
function MedPayModal({ modal, clientName, close, onNegotiationOpened }) {
  const { ins, projectId } = modal;

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false);
  // The address the waiver went to, once it has.
  const [emailed, setEmailed] = useState(null);

  const [form, setForm] = useState({
    carrier_name: ins?.carrier_name || '',
    carrier_address: '',
    subro_dept: 'Med-Pay Subrogation',
    carrier_fax: '',
    carrier_email: '',
    client_name: clientName || '',
    client_last_name: (clientName || '').split(' ').slice(-1)[0] || '',
    client_title: '',
    claim_number: ins?.claim_number || '',
    date_of_loss: '',
    settlement_amount: '',
    attorney_fees: '',
    medical_bills: '',
    costs: '',
    total_damages: '',
    medpay_paid: '',
    injury_summary: '',
    treatment_status: '',
    delivery_method: 'SENT VIA FAX',
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/projects/${projectId}/medpay-context`,
          { withCredentials: true }
        );
        if (!alive) return;
        setCtx(r.data);
        setForm((f) => ({
          ...f,
          settlement_amount: r.data.settlement_amount ?? '',
          attorney_fees: r.data.attorney_fees ?? '',
          costs: r.data.costs ?? '',
          medpay_paid: r.data.medpay_paid ?? '',
        }));
      } catch (e) {
        if (alive) setError('Could not load case figures: ' + (e.response?.data?.detail || e.message));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const money = (n) =>
    n === null || n === undefined || n === '' ? '—'
      : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const addressLines = (form.carrier_address || '').split('\n').filter(Boolean);

  // Recomputed live so the number in the letter is never a surprise.
  const cf = (() => {
    const gross = Number(form.settlement_amount) || 0;
    const mp = Number(form.medpay_paid) || 0;
    if (!gross || !mp) return null;
    const ratio = ((Number(form.attorney_fees) || 0) + (Number(form.costs) || 0)) / gross;
    return { ratio, reimbursement: Math.round(mp * (1 - ratio) * 100) / 100 };
  })();

  const narrativeIncomplete =
    !form.injury_summary.trim() || !form.treatment_status.trim();
  // What each action still needs, so a disabled button can say why rather
  // than just sitting there greyed out.
  const missingWaiver = [
    !form.carrier_name && 'carrier',
    addressLines.length === 0 && 'address',
    !form.total_damages && 'total damages',
  ].filter(Boolean);
  const missingCommonFund = [
    !form.carrier_name && 'carrier',
    addressLines.length === 0 && 'address',
    !form.settlement_amount && 'settlement',
    !form.medpay_paid && 'Med Pay paid',
  ].filter(Boolean);

  const canWaiver = missingWaiver.length === 0 && !busy;
  const canCommonFund = missingCommonFund.length === 0 && !busy;

  const download = async (path, body, filename) => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/${path}`,
        body,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Errors come back as a Blob because responseType is blob.
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const contact = {
    carrier_name: form.carrier_name,
    carrier_address_lines: addressLines,
    subro_dept: form.subro_dept || 'Med-Pay Subrogation',
    carrier_fax: form.carrier_fax || null,
    carrier_email: form.carrier_email || null,
    client_name: form.client_name,
    client_last_name: form.client_last_name || null,
    client_title: form.client_title || null,
    claim_number: form.claim_number || null,
    date_of_loss: form.date_of_loss || null,
    delivery_method: form.delivery_method,
  };

  const genWaiver = () => download('medpay-waiver', waiverPayload(),
    `MP Subro Waiver Request_${form.client_name}.docx`);

  // The same letter, sent instead of downloaded. One payload builder for
  // both, so what gets emailed and what would have been faxed cannot drift.
  const waiverPayload = () => ({
    ...contact,
    settlement_amount: num(form.settlement_amount),
    attorney_fees: num(form.attorney_fees),
    medical_bills: num(form.medical_bills),
    costs: num(form.costs),
    total_damages: Number(form.total_damages),
    injury_summary: form.injury_summary || null,
    treatment_status: form.treatment_status || null,
  });

  const emailWaiver = async () => {
    if (!form.carrier_email.trim()) {
      setError('Add the carrier\u2019s email address first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/medpay-waiver/email`,
        { ...waiverPayload(),
          recipient_email: form.carrier_email.trim(),
          negotiation_id: ins?.active_negotiation?.id || null,
          // Emailed, so the letter should not say it was faxed.
          delivery_method: 'SENT VIA EMAIL' },
        { withCredentials: true }
      );
      setEmailed(r.data.to);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Could not send it.');
    } finally {
      setBusy(false);
    }
  };

  const genCommonFund = () => download('medpay-common-fund', {
    ...contact,
    settlement_amount: Number(form.settlement_amount),
    attorney_fees: Number(form.attorney_fees) || 0,
    costs: Number(form.costs) || 0,
    medpay_paid: Number(form.medpay_paid),
  }, `MP Common Fund Reduction_${form.client_name}.docx`);

  const openNegotiation = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/projects/${projectId}/insurance/${ins.id}/start-negotiation`,
        {
          recipient_dept: form.subro_dept || 'Med-Pay Subrogation',
          recipient_address_lines: addressLines,
          recipient_fax: form.carrier_fax || null,
          recipient_email: form.carrier_email || null,
          medpay_paid_amount: Number(form.medpay_paid) || 0,
          requested_amount: cf ? cf.reimbursement : 0,
          settlement_amount: num(form.settlement_amount),
          legal_costs: num(form.costs),
          attorney_fees_percentage: ctx?.attorney_fees_pct ?? null,
          injury_summary: form.injury_summary || null,
          treatment_status: form.treatment_status || null,
        },
        { withCredentials: true }
      );
      setOpened(true);
      if (onNegotiationOpened) onNegotiationOpened();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not open the negotiation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    // Rendered inside the case overlay, which closes the case on any
    // click that reaches it. Without this every click in here — a
    // field, a label, anything — bubbled up and threw the preparer
    // back to the case list mid-form.
    <div style={s.backdrop} onClick={(e) => e.stopPropagation()}>
      <div style={s.modal}>
        <div style={s.title}>Med Pay reimbursement</div>
        <div style={s.subtitle}>
          {ins?.carrier_name}
          {ins?.claim_number ? ` · claim ${ins.claim_number}` : ''}
          {ins?.med_pay_limit ? ` · limit ${ins.med_pay_limit}` : ''}
        </div>

        {loading && <div style={s.loading}>Loading case figures…</div>}
        {error && <div style={s.error}>{error}</div>}
        {opened && (
          <div style={s.ok}>
            Reimbursement negotiation opened. Fax the letter, then record the
            outcome with Manual Accept on the negotiation.
          </div>
        )}

        {/* ---- subrogation contact: manual ---- */}
        <div style={s.section}>Subrogation contact</div>
        <div style={s.hint}>
          Not the adjuster on this row — they handle the claim, not
          reimbursement. Filevine does not hold this address.
        </div>
        <div style={{ ...s.row, marginTop: 8 }}>
          <Field label="Carrier" value={form.carrier_name} onChange={set('carrier_name')} required />
          <Field label="Attn" value={form.subro_dept} onChange={set('subro_dept')} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Address <span style={{ color: '#dc2626' }}>*</span></label>
          <textarea
            style={{ ...s.textarea, minHeight: 56 }}
            value={form.carrier_address}
            onChange={set('carrier_address')}
            placeholder={'PO BOX 231\nMonterey, CA 93942'}
          />
          <div style={s.hint}>One line per line.</div>
        </div>
        <div style={s.row}>
          <Field label="Fax" value={form.carrier_fax} onChange={set('carrier_fax')} />
          <Field label="Email" value={form.carrier_email} onChange={set('carrier_email')} />
        </div>
        {/* How it went. This prints at the head of the letter, under the
            date, and was fixed at fax — the field existed but nothing could
            change it, so a waiver emailed to a carrier still said it had
            been faxed. */}
        <div style={{ flex: 1, minWidth: 0, marginBottom: 10 }}>
          <label style={s.label} htmlFor="mp-delivery">Sent via</label>
          <select id="mp-delivery" style={s.input} value={form.delivery_method}
                  onChange={set('delivery_method')}>
            <option value="SENT VIA FAX">Fax</option>
            <option value="SENT VIA EMAIL">Email</option>
            <option value="SENT VIA FAX & EMAIL">Fax and email</option>
            <option value="SENT VIA U.S. MAIL">U.S. mail</option>
          </select>
          <div style={s.hint}>Prints under the date on both letters.</div>
        </div>

        {/* ---- client / claim ---- */}
        <div style={s.section}>Client &amp; claim</div>
        <div style={s.row}>
          <Field label="Client" value={form.client_name} onChange={set('client_name')} required />
          <Field label="Surname" value={form.client_last_name} onChange={set('client_last_name')} />
          <Field label="Title" value={form.client_title} onChange={set('client_title')} />
        </div>
        <div style={s.row}>
          <Field label="Claim #" value={form.claim_number} onChange={set('claim_number')} />
          <Field label="Date of loss" value={form.date_of_loss} onChange={set('date_of_loss')} placeholder="MM/DD/YYYY" />
        </div>

        {/* ---- figures ---- */}
        <div style={s.section}>Figures</div>
        <div style={s.row}>
          <Field label="Settlement" value={form.settlement_amount} onChange={set('settlement_amount')} />
          <Field label="Attorney's fees" value={form.attorney_fees} onChange={set('attorney_fees')} />
          <Field label="Costs" value={form.costs} onChange={set('costs')} />
        </div>
        <div style={s.row}>
          <Field label="Medical bills" value={form.medical_bills} onChange={set('medical_bills')} />
          <Field label="Med Pay paid" value={form.medpay_paid} onChange={set('medpay_paid')} />
          <Field label="Total damages" value={form.total_damages} onChange={set('total_damages')} required />
        </div>
        <div style={s.hint}>
          Fees are computed as settlement ÷ 3 for pre-lit cases (rule B8), not
          as 33.33%. Med Pay paid is summed from the Meds tab.
        </div>
        {!form.total_damages && (
          <div style={s.warn}>
            Total damages is required. The made-whole argument rests on total
            damages exceeding the recovery — without it the letter argues only
            from medical bills, which often come to less than the settlement.
          </div>
        )}
        {cf && (
          <div style={{ ...s.row, marginTop: 10 }}>
            <Fact label="Fees + costs" value={`${(cf.ratio * 100).toFixed(2)}%`} />
            <Fact label="Reduced reimbursement" value={money(cf.reimbursement)} />
          </div>
        )}

        {/* ---- narrative: manual ---- */}
        <div style={s.section}>Injuries &amp; treatment</div>
        <div style={s.hint}>Typed by the negotiator. Appears in the waiver letter.</div>
        <div style={{ marginTop: 6, marginBottom: 10 }}>
          <label style={s.label}>Injuries</label>
          <textarea
            style={{ ...s.textarea, minHeight: 56 }}
            value={form.injury_summary}
            onChange={set('injury_summary')}
            placeholder="injuries to her head, neck, chest, and lower back."
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Treatment status</label>
          <textarea
            style={{ ...s.textarea, minHeight: 56 }}
            value={form.treatment_status}
            onChange={set('treatment_status')}
            placeholder="She has undergone treatment for her symptoms but continues to have pain."
          />
        </div>
        {narrativeIncomplete && (
          <div style={s.warn}>
            Leave either blank and the letter ships with a bracketed
            instruction in it. The firm-rules check will refuse to send it,
            but it will still download — don't fax it like that.
          </div>
        )}

        {emailed && (
          <div style={s.ok}>
            Waiver request emailed to <strong>{emailed}</strong>. The letter
            went as an attachment and is recorded on the negotiation — the
            carrier replies by phone or email, and you record the outcome
            with Manual Accept as before.
          </div>
        )}

        {/* ---- actions ---- */}
        <div style={s.section}>Letters</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...s.cancel, opacity: canWaiver ? 1 : 0.5 }}
                  onClick={genWaiver} disabled={!canWaiver}>
            1 · Waiver request
          </button>
          {/* The same letter, sent. Carriers take this one by email; the
              common fund letter and the negotiation stay as they were,
              because a subrogation department does not click accept and
              reject links and the outcome still comes back by hand. */}
          <button style={{ ...s.cancel,
                           opacity: canWaiver && form.carrier_email.trim() && !busy ? 1 : 0.5 }}
                  onClick={emailWaiver}
                  disabled={!canWaiver || !form.carrier_email.trim() || busy}
                  title={form.carrier_email.trim()
                    ? `Email it to ${form.carrier_email.trim()}`
                    : 'Add the carrier email address above first'}>
            {busy ? 'Sending…' : 'Email the waiver request'}
          </button>
          <button style={{ ...s.cancel, opacity: canCommonFund ? 1 : 0.5 }}
                  onClick={genCommonFund} disabled={!canCommonFund}>
            2 · Common fund reduction
          </button>
          <button style={{ ...s.cancel, opacity: canCommonFund && !opened ? 1 : 0.5 }}
                  onClick={openNegotiation} disabled={!canCommonFund || opened}>
            Open reimbursement negotiation
          </button>
        </div>
        {(missingWaiver.length > 0 || missingCommonFund.length > 0) && (
          <div style={s.warn}>
            {missingWaiver.length > 0 && (
              <div>
                <b>Waiver request</b> needs: {missingWaiver.join(', ')}
              </div>
            )}
            {missingCommonFund.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <b>Common fund &amp; negotiation</b> need: {missingCommonFund.join(', ')}
              </div>
            )}
            <div style={{ marginTop: 6, opacity: .85 }}>
              Address and total damages are typed here. Settlement and Med Pay
              paid come from the case — set them on Case Financials and the
              Meds tab if they are blank.
            </div>
          </div>
        )}
        <div style={s.hint}>
          Letters go out by fax, so nothing here emails the carrier. Open the
          negotiation to track it, then use Manual Accept to record what they
          agree to.
        </div>

        <div style={s.actions}>
          <button style={s.primary} onClick={close} disabled={busy}>
            {busy ? 'Working…' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder }) {
  return (
    <div style={{ flex: 1, minWidth: 0, marginBottom: 10 }}>
      <label style={s.label}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      <input style={s.input} value={value ?? ''} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={s.label}>{label}</div>
      <div style={{ fontSize: 15, color: '#171D29', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(720px, 95vw)', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 18, color: '#171D29', marginBottom: 4,
  },
  subtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 8 },
  loading: { fontSize: 12, color: '#6b7585', marginBottom: 10 },
  error: {
    background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
    borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.45,
  },
  warn: {
    background: '#fef3c7', color: '#92400e', padding: '8px 12px',
    borderRadius: 6, marginTop: 6, marginBottom: 8, fontSize: 12, lineHeight: 1.45,
  },
  ok: {
    background: '#ecfdf5', color: '#065f46', padding: '8px 12px',
    borderRadius: 6, marginBottom: 12, fontSize: 12, lineHeight: 1.45,
  },
  section: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 11, color: '#A1B808', letterSpacing: 1,
    textTransform: 'uppercase', margin: '16px 0 8px 0',
    borderTop: '1px solid #f3f4f6', paddingTop: 12,
  },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  label: {
    display: 'block', fontSize: 10, color: '#6b7585',
    textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit', resize: 'vertical',
  },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  cancel: {
    padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  primary: {
    padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default MedPayModal;
